/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface BaseMedicalControlContactProps {
  id: string;
  personId: string;
  rank: number;
  specialty: string | null;
  baseId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class BaseMedicalControlContactRecord extends Record<BaseMedicalControlContactProps>({
  id: null as any,
  personId: null as any,
  rank: null as any,
  specialty: null as any,
  baseId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    personId: string;
    rank: number;
    specialty: string | null;
    baseId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): BaseMedicalControlContactRecord {
    if (json == null) {
      throw new Error('BaseMedicalControlContactRecord.fromJS: parameter cannot be null');
    }
    return new BaseMedicalControlContactRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'BaseMedicalControlContactJson.id',
      ),
      personId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
        'BaseMedicalControlContactJson.personId',
      ),
      rank: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'rank', 0),
        'BaseMedicalControlContactJson.rank',
      ),
      specialty: json.specialty,
      baseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
        'BaseMedicalControlContactJson.baseId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'BaseMedicalControlContactJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    personId: string;
    rank: number;
    specialty: string | null;
    baseId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [BaseMedicalControlContactRecord, Error[]] {
    if (json == null) {
      throw new Error('BaseMedicalControlContactRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new BaseMedicalControlContactRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'BaseMedicalControlContactJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
            'BaseMedicalControlContactJson.personId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rank: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'rank', 0),
            'BaseMedicalControlContactJson.rank',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialty: tryOrDefault(
        () => json.specialty,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
            'BaseMedicalControlContactJson.baseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'BaseMedicalControlContactJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: BaseMedicalControlContactProps) {
    super(props);
    if (props == null) {
      throw new Error('BaseMedicalControlContactRecord constructor parameter cannot be null');
    }
  }
}

export abstract class BaseMedicalControlContactViewModelBase
  implements BaseMedicalControlContactProps
{
  protected readonly _props: BaseMedicalControlContactProps;
  private _json?: BaseMedicalControlContactProps;

  get id(): string {
    return this._props.id;
  }
  get personId(): string {
    return this._props.personId;
  }
  get rank(): number {
    return this._props.rank;
  }
  get specialty(): string | null {
    return this._props.specialty;
  }
  get baseId(): string {
    return this._props.baseId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: BaseMedicalControlContactProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        personId: this._props.personId,
        rank: this._props.rank,
        specialty: this._props.specialty,
        baseId: this._props.baseId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllBaseMedicalControlContactEntities = (state: AppState) =>
  state.entityStores.baseMedicalControlContact.entities;

export const selectBaseMedicalControlContactById = (id: string) => (state: AppState) =>
  state.entityStores.baseMedicalControlContact.entities.get(id);

export const selectBaseMedicalControlContactMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.baseMedicalControlContact.meta.get(id);

export const selectBaseMedicalControlContactAndMetadataById = (id: string) =>
  createSelector(
    selectBaseMedicalControlContactById(id),
    selectBaseMedicalControlContactMetadataById(id),
    (baseMedicalControlContact, meta) => ({ baseMedicalControlContact, meta }),
  );

export function selectBaseMedicalControlContactEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllBaseMedicalControlContactEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, BaseMedicalControlContactRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectBaseMedicalControlContactEntitiesByPersonId(personId: string) {
  return createSelector(selectAllBaseMedicalControlContactEntities, (entities) =>
    entities.filter((e) => e.personId === personId).valueSeq(),
  );
}

export function selectBaseMedicalControlContactEntitiesByRank(rank: number) {
  return createSelector(selectAllBaseMedicalControlContactEntities, (entities) =>
    entities.filter((e) => e.rank === rank).valueSeq(),
  );
}

export function selectBaseMedicalControlContactEntitiesBySpecialty(specialty: string) {
  return createSelector(selectAllBaseMedicalControlContactEntities, (entities) =>
    entities.filter((e) => e.specialty === specialty).valueSeq(),
  );
}

export function selectBaseMedicalControlContactEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllBaseMedicalControlContactEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectBaseMedicalControlContactEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllBaseMedicalControlContactEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
